import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, Crown, Gem, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import {
  deleteNotification,
  fetchNotifications,
  markNotificationRead,
  relativeTime,
  type AppNotification,
} from "@/lib/notifications";

function iconFor(kind: string) {
  if (kind === "vip") return Gem;
  if (kind === "premium") return Crown;
  return BellRing;
}

function NotificationsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, loading } = useAuth();

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: fetchNotifications,
    enabled: Boolean(user),
  });

  const open = async (n: AppNotification) => {
    if (!n.read) {
      await markNotificationRead(n.id).catch(() => undefined);
      qc.invalidateQueries({ queryKey: ["notifications"] });
    }
    if (n.link) navigate({ to: n.link } as never);
  };

  const remove = async (id: string) => {
    await deleteNotification(id).catch(() => undefined);
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  return (
    <div className="container-x max-w-2xl py-10">
      <h1 className="text-3xl font-extrabold tracking-tight">Bildirişlər</h1>

      {!loading && !user && (
        <p className="mt-6 rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Bildirişləri görmək üçün{" "}
          <Link to="/giris" className="font-semibold text-primary">
            daxil olun
          </Link>
          .
        </p>
      )}

      {user && isLoading && (
        <div className="mt-10 flex justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {user && !isLoading && (
        <div className="mt-6 space-y-3">
          {items.length === 0 && (
            <p className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              Hazırda bildiriş yoxdur.
            </p>
          )}
          {items.map((n, i) => {
            const Icon = iconFor(n.kind);
            return (
              <div
                key={n.id}
                role="button"
                tabIndex={0}
                onClick={() => open(n)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    open(n);
                  }
                }}
                style={{ animationDelay: `${i * 60}ms` }}
                className={cn(
                  "relative flex animate-fade-up cursor-pointer gap-4 rounded-2xl border border-border p-4 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-elevated",
                  n.read ? "bg-muted/40" : "bg-card",
                )}
              >
                {!n.read && (
                  <span
                    aria-label="Oxunmamış"
                    className="absolute right-10 top-4 size-2 rounded-full bg-destructive"
                  />
                )}
                <button
                  type="button"
                  aria-label="Bildirişi sil"
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(n.id);
                  }}
                  className="absolute right-2 top-2 grid size-7 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-destructive hover:text-destructive-foreground"
                >
                  <X className="size-4" />
                </button>
                <span
                  className={cn(
                    "grid size-10 shrink-0 place-items-center rounded-full",
                    n.kind === "vip" || n.kind === "premium"
                      ? "bg-amber-500/15 text-amber-500"
                      : "bg-primary/15 text-primary",
                  )}
                >
                  <Icon className="size-5" />
                </span>
                <div className="min-w-0 pr-6">
                  <p className={cn("font-semibold", n.read && "text-muted-foreground")}>
                    {n.title}
                  </p>
                  <p className="text-sm text-muted-foreground">{n.body}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {relativeTime(n.created_at)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute("/bildirisler")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Bildirişlər — Albazar.az" },
      { name: "description", content: "Elanlarınız və mesajlarınız haqqında bütün bildirişlər." },
      { property: "og:title", content: "Bildirişlər — Albazar.az" },
      { property: "og:description", content: "Bütün bildirişləriniz bir yerdə." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: NotificationsPage,
});
